import React, { useState } from 'react';
import { Card, Form, Button, Alert, Table, Badge, ProgressBar, Spinner, Row, Col, Modal } from 'react-bootstrap';
import Papa from 'papaparse';
import { useAuth } from '../../context/AuthContext';
import { createProduct, getProducts, getProductById } from '../../services/productApi';
import { createItem, getItems } from '../../services/itemApi';
import { getCategoriesByType } from '../../services/categoryApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import {
  generateProductsTemplate,
  generateItemsTemplate,
  generateCombinedTemplate,
  downloadCSV,
  getExpectedColumns,
  generateExcelItemsTemplate,
  generateExcelProductsTemplate,
  generateExcelCombinedTemplate,
  downloadExcel,
  parseExcelFile
} from '../../utils/csvTemplateGenerator';
import {
  validateProductData,
  validateItemData,
  validateCombinedData,
  validateColumns,
  validateFileSize,
  validateRowCount
} from '../../utils/csvValidator';

const BulkImportTab = ({ showAlert, onImportComplete }) => {
  const { user } = useAuth();
  const [importType, setImportType] = useState('excel-items');
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [importResults, setImportResults] = useState({ success: 0, errors: [], createdItems: [], createdProducts: [] });
  const [excelData, setExcelData] = useState({ products: [], items: [] });
  
  // Estado para generación de plantilla
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [systemData, setSystemData] = useState(null);
  
  // Cache para evitar consultas repetidas
  const [categoriesCache, setCategoriesCache] = useState({});
  const [productsCache, setProductsCache] = useState({});
  const [colorsCache, setColorsCache] = useState({});
  const [sizesCache, setSizesCache] = useState({});
  const [weightsCache, setWeightsCache] = useState({});
  const [defaultSupplierId, setDefaultSupplierId] = useState(null);

  /**
   * Maneja el cambio de tipo de importación
   */
  const handleImportTypeChange = (e) => {
    setImportType(e.target.value);
    // Limpiar datos al cambiar tipo
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setValidationResults(null);
  };

  /**
   * Carga los datos del sistema para la plantilla
   */
  const loadSystemData = async () => {
    setGeneratingTemplate(true);
    try {
      // Cargar en paralelo todos los datos necesarios
      const [productsRes, colorsRes, sizesRes, weightsRes, categoryTypesRes] = await Promise.all([
        getProducts(),
        dotoniwoofApi.get('/color.php'),
        dotoniwoofApi.get('/size.php'),
        dotoniwoofApi.get('/weight.php'),
        dotoniwoofApi.get('/categoryType.php')
      ]);

      // Procesar productos
      const productsData = productsRes.data?.message || [];
      const products = Array.isArray(productsData) ? productsData : productsData?.products || [];

      // Procesar atributos
      const colors = colorsRes.data?.message || [];
      const sizes = sizesRes.data?.message || [];
      const weights = weightsRes.data?.message || [];

      // Procesar categorías con sus tipos
      const categoryTypes = categoryTypesRes.data?.message || [];
      let allCategories = [];
      
      for (const type of categoryTypes) {
        try {
          const categoriesRes = await getCategoriesByType(type.id_category_type);
          const categories = categoriesRes.data?.message || [];
          if (Array.isArray(categories)) {
            allCategories = [
              ...allCategories,
              ...categories.map(c => ({
                ...c,
                categoryTypeName: type.categoryTypeName
              }))
            ];
          }
        } catch (e) {
          console.error('Error cargando categorías:', e);
        }
      }

      const data = {
        products,
        colors,
        sizes,
        weights,
        categories: allCategories
      };

      setSystemData(data);
      return data;
    } catch (error) {
      console.error('Error cargando datos del sistema:', error);
      showAlert('Error al cargar datos del sistema para la plantilla', 'error');
      return null;
    } finally {
      setGeneratingTemplate(false);
    }
  };

  /**
   * Descarga la plantilla según el tipo seleccionado
   */
  const handleDownloadTemplate = async () => {
    // Para Excel, necesitamos cargar datos del sistema primero
    if (importType.startsWith('excel-')) {
      showAlert('Cargando datos del sistema...', 'info');
      const data = await loadSystemData();
      
      if (!data) {
        showAlert('No se pudieron cargar los datos. Intenta de nuevo.', 'error');
        return;
      }

      let workbook, filename, message;

      switch (importType) {
        case 'excel-items':
          workbook = generateExcelItemsTemplate(data);
          filename = 'plantilla_items.xlsx';
          message = `Plantilla descargada con ${data.products.length} productos de referencia y atributos disponibles.`;
          break;
        case 'excel-products':
          workbook = generateExcelProductsTemplate(data);
          filename = 'plantilla_productos.xlsx';
          message = `Plantilla descargada con ${data.categories.length} categorías de referencia.`;
          break;
        case 'excel-combined':
          workbook = generateExcelCombinedTemplate(data);
          filename = 'plantilla_productos_items.xlsx';
          message = `Plantilla descargada con categorías y atributos de referencia.`;
          break;
        default:
          return;
      }

      downloadExcel(workbook, filename);
      showAlert(message, 'success');
    } else {
      // CSV (legacy)
      let csvContent, filename;

      switch (importType) {
        case 'products':
          csvContent = generateProductsTemplate();
          filename = 'plantilla_productos.csv';
          break;
        case 'items':
          csvContent = generateItemsTemplate();
          filename = 'plantilla_items.csv';
          break;
        case 'combined':
          csvContent = generateCombinedTemplate();
          filename = 'plantilla_combinada.csv';
          break;
        default:
          return;
      }

      downloadCSV(csvContent, filename);
      showAlert('Plantilla CSV descargada exitosamente', 'success');
    }
  };

  /**
   * Maneja la carga del archivo (Excel o CSV)
   */
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    
    if (!file) return;

    // Validar tamaño
    const sizeValidation = validateFileSize(file, 10); // 10MB para Excel
    if (!sizeValidation.isValid) {
      showAlert(sizeValidation.error, 'error');
      return;
    }

    setCsvFile(file);

    // Detectar tipo de archivo
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCSV = file.name.endsWith('.csv');

    if (!isExcel && !isCSV) {
      showAlert('Por favor selecciona un archivo Excel (.xlsx) o CSV (.csv)', 'error');
      return;
    }

    try {
      if (isExcel) {
        // Parsear Excel
        const excelResult = await parseExcelFile(file);
        console.log('Excel parseado:', excelResult);

        // Extraer datos según tipo de importación (ignorar hojas de referencia)
        let products = [];
        let items = [];

        if (importType === 'excel-items') {
          // Solo importar items (ignorar hoja de productos de referencia)
          items = excelResult.items || [];
        } else if (importType === 'excel-products') {
          // Solo importar productos
          products = excelResult.products || [];
        } else if (importType === 'excel-combined') {
          // Importar ambos
          products = excelResult.products || [];
          items = excelResult.items || [];
        }

        setExcelData({ products, items });

        // Validar datos
        validateExcelData({ products, items });
      } else {
        // Parsear CSV (legacy)
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          encoding: 'UTF-8',
          complete: (results) => {
            console.log('CSV parseado:', results);
            
            const rowValidation = validateRowCount(results.data.length);
            if (!rowValidation.isValid) {
              showAlert(rowValidation.error, 'error');
              return;
            }

            setCsvHeaders(results.meta.fields);
            setCsvData(results.data);
            validateCSVData(results.meta.fields, results.data);
          },
          error: (error) => {
            console.error('Error al parsear CSV:', error);
            showAlert('Error al leer el archivo CSV. Verifica que esté correctamente formateado.', 'error');
          }
        });
      }
    } catch (error) {
      console.error('Error al procesar archivo:', error);
      showAlert('Error al leer el archivo. Verifica que esté correctamente formateado.', 'error');
    }
  };

  /**
   * Valida los datos del CSV
   */
  const validateCSVData = (headers, data) => {
    const expectedColumns = getExpectedColumns(importType);
    
    // Validar columnas
    const columnValidation = validateColumns(headers, expectedColumns);
    
    // Validar datos según tipo
    let dataValidation;
    switch (importType) {
      case 'products':
        dataValidation = validateProductData(data);
        break;
      case 'items':
        dataValidation = validateItemData(data);
        break;
      case 'combined':
        dataValidation = validateCombinedData(data);
        break;
      default:
        dataValidation = { errors: [], warnings: [], isValid: true };
    }

    setValidationResults({
      columnValidation,
      dataValidation,
      isValid: columnValidation.isValid && dataValidation.isValid
    });

    if (!columnValidation.isValid || !dataValidation.isValid) {
      showAlert('Se encontraron errores en el archivo CSV. Revisa la vista previa.', 'warning');
    } else {
      showAlert(`Archivo validado exitosamente. ${data.length} registros listos para importar.`, 'success');
    }
  };

  /**
   * Valida los datos del Excel
   */
  const validateExcelData = (excelResult) => {
    const { products, items } = excelResult;
    
    // Validar según tipo de importación
    if (importType === 'excel-items' && items.length === 0) {
      showAlert('La hoja "Items" está vacía. Agrega los items que deseas importar.', 'error');
      setValidationResults({ isValid: false, dataValidation: { errors: [], warnings: [] } });
      return;
    }

    if (importType === 'excel-products' && products.length === 0) {
      showAlert('La hoja "Productos" está vacía. Agrega los productos que deseas crear.', 'error');
      setValidationResults({ isValid: false, dataValidation: { errors: [], warnings: [] } });
      return;
    }

    if (importType === 'excel-combined' && products.length === 0 && items.length === 0) {
      showAlert('Las hojas "Productos" e "Items" están vacías.', 'error');
      setValidationResults({ isValid: false, dataValidation: { errors: [], warnings: [] } });
      return;
    }

    // Validar productos (solo si aplica)
    const productsValidation = (importType === 'excel-products' || importType === 'excel-combined') && products.length > 0 
      ? validateProductData(products) 
      : { errors: [], warnings: [], isValid: true };

    // Validar items (solo si aplica)
    const itemsValidation = (importType === 'excel-items' || importType === 'excel-combined') && items.length > 0 
      ? validateItemData(items) 
      : { errors: [], warnings: [], isValid: true };

    // Combinar errores y warnings
    const allErrors = [
      ...productsValidation.errors.map(e => ({ ...e, sheet: 'Productos' })),
      ...itemsValidation.errors.map(e => ({ ...e, sheet: 'Items' }))
    ];

    const allWarnings = [
      ...productsValidation.warnings.map(w => ({ ...w, sheet: 'Productos' })),
      ...itemsValidation.warnings.map(w => ({ ...w, sheet: 'Items' }))
    ];

    const isValid = productsValidation.isValid && itemsValidation.isValid;

    setValidationResults({
      dataValidation: {
        errors: allErrors,
        warnings: allWarnings,
        isValid
      },
      isValid,
      productsCount: importType === 'excel-items' ? 0 : products.length,
      itemsCount: importType === 'excel-products' ? 0 : items.length
    });

    if (!isValid) {
      showAlert('Se encontraron errores en el archivo Excel. Revisa la vista previa.', 'warning');
    } else {
      let message = 'Archivo validado exitosamente. ';
      if (importType === 'excel-items') {
        message += `${items.length} items listos para importar.`;
      } else if (importType === 'excel-products') {
        message += `${products.length} productos listos para crear.`;
      } else {
        message += `${products.length} productos y ${items.length} items listos para importar.`;
      }
      showAlert(message, 'success');
    }
  };

  /**
   * Obtiene o crea una categoría
   */
  const getCategoryId = async (categoryName, categoryTypeName) => {
    const cacheKey = `${categoryTypeName}-${categoryName}`;
    
    if (categoriesCache[cacheKey]) {
      return categoriesCache[cacheKey];
    }

    try {
      // Primero obtener el tipo de categoría
      const typesResponse = await dotoniwoofApi.get('/categoryType.php');
      const categoryType = typesResponse.data.message.find(
        type => type.categoryTypeName.toLowerCase() === categoryTypeName.toLowerCase()
      );

      if (!categoryType) {
        throw new Error(`Tipo de categoría "${categoryTypeName}" no encontrado`);
      }

      // Obtener categorías de ese tipo
      const categoriesResult = await getCategoriesByType(categoryType.id_category_type);
      const categories = categoriesResult.data.message;
      
      const category = Array.isArray(categories) 
        ? categories.find(cat => cat.category_name?.toLowerCase() === categoryName.toLowerCase())
        : null;

      if (category) {
        const categoryId = category.id_category;
        setCategoriesCache(prev => ({ ...prev, [cacheKey]: categoryId }));
        return categoryId;
      }

      throw new Error(`Categoría "${categoryName}" no encontrada`);
    } catch (error) {
      console.error('Error obteniendo categoría:', error);
      throw error;
    }
  };

  /**
   * Obtiene id_product e id_supplier por ID (Excel: hoja Items usa id_product)
   */
  const getProductInfoById = async (id) => {
    const key = `id:${id}`;
    if (productsCache[key]) {
      return productsCache[key];
    }
    try {
      const result = await getProductById(id);
      if (!result.ok) throw new Error(result.data?.message || 'Error al obtener producto');
      const product = result.data?.message ?? result.data?.data ?? result.data;
      if (!product) throw new Error(`Producto con ID ${id} no encontrado`);
      const info = {
        id_product: product.id_product ?? product.id ?? id,
        id_supplier: product.id_supplier ?? product.supplierId ?? null
      };
      setProductsCache(prev => ({ ...prev, [key]: info }));
      return info;
    } catch (error) {
      console.error('Error obteniendo producto por ID:', error);
      throw error;
    }
  };

  /**
   * Obtiene id_product e id_supplier por nombre (CSV legacy)
   */
  const getProductInfo = async (productName) => {
    if (productsCache[productName]) {
      return productsCache[productName];
    }
    try {
      const result = await getProducts();
      const products = result.data.message;
      const productList = Array.isArray(products) ? products : products?.products || [];
      const product = productList.find(
        p => (p.name || p.product_name || p.nombre_producto || '').toLowerCase() === (productName || '').toLowerCase()
      );
      if (product) {
        const info = {
          id_product: product.id_product ?? product.id,
          id_supplier: product.id_supplier ?? product.supplierId ?? null
        };
        setProductsCache(prev => ({ ...prev, [productName]: info }));
        return info;
      }
      throw new Error(`Producto "${productName}" no encontrado`);
    } catch (error) {
      console.error('Error obteniendo producto:', error);
      throw error;
    }
  };

  /**
   * Obtiene o crea un atributo (color, talla, gramaje)
   */
  const getAttributeId = async (attributeType, attributeName) => {
    if (!attributeName || attributeName === 'N/A') return null;

    const cacheKey = `${attributeType}-${attributeName}`;
    const cacheMap = {
      color: [colorsCache, setColorsCache],
      size: [sizesCache, setSizesCache],
      weight: [weightsCache, setWeightsCache]
    };

    const [cache, setCache] = cacheMap[attributeType];
    
    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    try {
      const endpoint = `/${attributeType}.php`;
      const response = await dotoniwoofApi.get(endpoint);
      const attributes = response.data.message;
      
      const nameField = `${attributeType}_name`;
      const idField = `id_${attributeType}`;
      
      const attribute = attributes.find(
        attr => attr[nameField]?.toLowerCase() === attributeName.toLowerCase()
      );

      if (attribute) {
        const attributeId = attribute[idField];
        setCache(prev => ({ ...prev, [cacheKey]: attributeId }));
        return attributeId;
      }

      // Si no existe, crearlo
      const createResponse = await dotoniwoofApi.post(endpoint, {
        firebase_UID: user.uid,
        [nameField]: attributeName
      });

      if (createResponse.data.code === 201) {
        const newId = createResponse.data.id || createResponse.data.message?.id;
        setCache(prev => ({ ...prev, [cacheKey]: newId }));
        return newId;
      }

      return null;
    } catch (error) {
      console.error(`Error con atributo ${attributeType}:`, error);
      return null;
    }
  };

  /**
   * Obtiene el id del primer proveedor (para ítems cuando el producto no tiene proveedor)
   */
  const ensureDefaultSupplier = async () => {
    if (defaultSupplierId != null) return defaultSupplierId;
    try {
      const { data } = await dotoniwoofApi.get('/supplier.php');
      const list = data?.message ?? data?.data;
      const suppliers = Array.isArray(list) ? list : [];
      const first = suppliers[0];
      const id = first?.id_supplier ?? first?.id ?? null;
      if (id != null) setDefaultSupplierId(id);
      return id;
    } catch (e) {
      console.warn('No se pudo cargar lista de proveedores:', e);
      return null;
    }
  };

  /**
   * Importa los datos (Excel o CSV)
   */
  const handleImport = async () => {
    if (!validationResults?.isValid) {
      showAlert('Corrige los errores antes de importar', 'error');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    const results = { success: 0, errors: [], createdItems: [], createdProducts: [] };

    try {
      // Si vamos a importar ítems, obtener proveedor por defecto (el estado se actualiza después)
      let fallbackSupplierId = defaultSupplierId;
      const needsItems = (importType.startsWith('excel-') && (importType === 'excel-items' || importType === 'excel-combined') && excelData.items?.length > 0) ||
        (['items', 'combined'].includes(importType) && csvData.length > 0);
      if (needsItems) {
        fallbackSupplierId = await ensureDefaultSupplier();
      }

      if (importType.startsWith('excel-')) {
        // Importar desde Excel
        const totalRecords = excelData.products.length + excelData.items.length;
        let currentRecord = 0;

        // Primero importar productos (si aplica)
        if (importType === 'excel-products' || importType === 'excel-combined') {
          for (let i = 0; i < excelData.products.length; i++) {
            const row = excelData.products[i];
            const rowNumber = i + 2;

            try {
              const created = await importProduct(row, rowNumber);
              results.success++;
              if (created) results.createdProducts.push(created);
            } catch (error) {
              console.error(`Error en Productos fila ${rowNumber}:`, error);
              results.errors.push({
                sheet: 'Productos',
                row: rowNumber,
                message: error.message || 'Error desconocido'
              });
            }

            currentRecord++;
            setImportProgress((currentRecord / totalRecords) * 100);
          }
        }

        // Luego importar items (si aplica)
        if (importType === 'excel-items' || importType === 'excel-combined') {
          for (let i = 0; i < excelData.items.length; i++) {
            const row = excelData.items[i];
            const rowNumber = i + 2;

            try {
              const created = await importItem(row, rowNumber, fallbackSupplierId);
              results.success++;
              if (created) results.createdItems.push(created);
            } catch (error) {
              console.error(`Error en Items fila ${rowNumber}:`, error);
              results.errors.push({
                sheet: 'Items',
                row: rowNumber,
                message: error.message || 'Error desconocido'
              });
            }

            currentRecord++;
            setImportProgress((currentRecord / totalRecords) * 100);
          }
        }
      } else {
        // Importar desde CSV (legacy)
        for (let i = 0; i < csvData.length; i++) {
          const row = csvData[i];
          const rowNumber = i + 2;

          try {
            let created = null;
            if (importType === 'products') {
              created = await importProduct(row, rowNumber);
            } else if (importType === 'items') {
              created = await importItem(row, rowNumber, fallbackSupplierId);
            } else if (importType === 'combined') {
              if (row.tipo?.toUpperCase() === 'PRODUCTO') {
                created = await importProduct(row, rowNumber);
              } else if (row.tipo?.toUpperCase() === 'ITEM') {
                created = await importItem(row, rowNumber, fallbackSupplierId);
              }
            }

            results.success++;
            if (created) {
              if (created.type === 'product') results.createdProducts.push(created);
              else results.createdItems.push(created);
            }
          } catch (error) {
            console.error(`Error en fila ${rowNumber}:`, error);
            results.errors.push({
              row: rowNumber,
              message: error.message || 'Error desconocido'
            });
          }

          setImportProgress(((i + 1) / csvData.length) * 100);
        }
      }

      setImportResults(results);
      setShowResults(true);

      if (results.errors.length === 0) {
        showAlert(`¡Importación completada! ${results.success} registros importados exitosamente.`, 'success');
      } else {
        showAlert(
          `Importación completada con errores. ${results.success} exitosos, ${results.errors.length} fallidos.`,
          'warning'
        );
      }

      if (onImportComplete) {
        onImportComplete();
      }

    } catch (error) {
      console.error('Error en importación:', error);
      showAlert('Error durante la importación', 'error');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Importa un producto
   */
  const importProduct = async (row, rowNumber) => {
    const categoryId = await getCategoryId(
      row.categoria,
      row.tipo_categoria
    );

    const payload = {
      firebase_UID: user.uid,
      name: (row.nombre_producto != null ? String(row.nombre_producto) : '').trim(),
      description: (row.descripcion_producto != null ? String(row.descripcion_producto) : '').trim(),
      id_category: categoryId
    };

    const result = await createProduct(payload);
    
    if (!result.ok || (result.data?.code !== 201 && result.data?.code !== 200)) {
      throw new Error(result.data?.message || 'Error al crear producto');
    }

    // Guardar en cache (objeto con id_product e id_supplier para uso en items)
    const productId = result.data.id || result.data.message?.id;
    const productName = (row.nombre_producto != null ? String(row.nombre_producto) : '').trim();
    setProductsCache(prev => ({ ...prev, [productName]: { id_product: productId, id_supplier: null } }));

    return { type: 'product', row: rowNumber, sheet: 'Productos', name: productName };
  };

  /**
   * Importa un item (el backend exige firebase_UID, id_product, id_supplier, stock, price)
   * Excel: row.id_product + row.producto (referencia). CSV: row.nombre_producto
   * @param {Object} row - Fila del Excel/CSV
   * @param {number} rowNumber - Número de fila (para errores)
   * @param {number|null} fallbackSupplierId - id_supplier por defecto si el producto no tiene
   */
  const importItem = async (row, rowNumber, fallbackSupplierId = null) => {
    const rawId = row.id_product != null ? row.id_product : row.id_producto;
    const hasId = rawId !== '' && rawId !== undefined && rawId !== null && !Number.isNaN(Number(rawId));
    const productInfo = hasId
      ? await getProductInfoById(Number(rawId))
      : await getProductInfo(row.nombre_producto || row.producto);
    const { id_product: productId, id_supplier: productSupplierId } = productInfo;
    // Backend exige id_supplier; usar el del producto o el pasado como fallback
    const supplierId = productSupplierId ?? fallbackSupplierId ?? defaultSupplierId;
    if (supplierId == null) {
      throw new Error('El producto no tiene proveedor y no hay proveedores en el sistema. Crea al menos un proveedor o asígnale uno al producto.');
    }

    const colorId = await getAttributeId('color', row.color);
    const sizeId = await getAttributeId('size', row.talla);
    const weightId = await getAttributeId('weight', row.gramaje);

    const precioVal = row.precio != null ? parseFloat(String(row.precio).replace(',', '.')) : NaN;
    const stockVal = row.stock != null ? parseInt(String(row.stock), 10) : NaN;
    if (isNaN(precioVal) || precioVal < 0) {
      throw new Error('El precio debe ser un número válido mayor o igual a 0');
    }
    if (isNaN(stockVal) || stockVal < 0) {
      throw new Error('El stock debe ser un número entero mayor o igual a 0');
    }

    const payload = {
      firebase_UID: user.uid,
      id_product: productId,
      id_supplier: supplierId,
      description: (row.descripcion_item != null ? String(row.descripcion_item) : '').trim(),
      sku: (row.sku != null ? String(row.sku) : '').trim() || undefined,
      price: precioVal,
      stock: stockVal,
      id_color: colorId || undefined,
      id_size: sizeId || undefined,
      id_weight: weightId || undefined,
      url: undefined
    };

    const result = await createItem(payload);
    
    if (!result.ok || (result.data?.code !== 201 && result.data?.code !== 200)) {
      const msg = result.data?.message || 'Error al crear item';
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }

    const productLabel = row.producto ?? row.nombre_producto ?? '';
    return {
      type: 'item',
      row: rowNumber,
      sheet: 'Items',
      description: payload.description,
      product: productLabel,
      price: payload.price,
      stock: payload.stock,
      sku: payload.sku || '-'
    };
  };

  /**
   * Reinicia el formulario
   */
  const handleReset = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setExcelData({ products: [], items: [] });
    setValidationResults(null);
    setImportProgress(0);
    setImportResults({ success: 0, errors: [], createdItems: [], createdProducts: [] });
    const fileInput = document.getElementById('fileInput');
    if (fileInput) fileInput.value = '';
  };

  return (
    <>
      <Card className="modern-card">
        <Card.Header className="modern-card-header">
          <h4 className="mb-0">
            <i className="fas fa-file-upload me-2"></i>
            Importación Masiva de Productos e Items
          </h4>
        </Card.Header>
        <Card.Body>
          {/* Paso 1: Seleccionar tipo */}
          <div className="import-step mb-4">
            <h5 className="step-title">
              <Badge bg="primary" className="me-2">1</Badge>
              Selecciona el Tipo de Importación
            </h5>
            <Form.Group>
              <Form.Select 
                value={importType} 
                onChange={handleImportTypeChange}
                className="modern-select"
                disabled={importing}
              >
                <optgroup label="📊 Excel (Recomendado)">
                  <option value="excel-items">📦 Excel - Crear Items (con productos de referencia)</option>
                  <option value="excel-products">📋 Excel - Crear Productos (con categorías de referencia)</option>
                  <option value="excel-combined">📊 Excel - Crear Productos + Items</option>
                </optgroup>
                <optgroup label="📄 CSV (Legacy)">
                  <option value="items">CSV - Solo Items</option>
                  <option value="products">CSV - Solo Productos</option>
                  <option value="combined">CSV - Productos + Items</option>
                </optgroup>
              </Form.Select>
              <Form.Text className="text-muted">
                {importType === 'excel-items' && '📦 La plantilla incluirá los PRODUCTOS EXISTENTES y ATRIBUTOS como referencia. Solo necesitas llenar la hoja "Items".'}
                {importType === 'excel-products' && '📋 La plantilla incluirá las CATEGORÍAS EXISTENTES como referencia. Solo necesitas llenar la hoja "Productos".'}
                {importType === 'excel-combined' && '📊 La plantilla incluirá CATEGORÍAS y ATRIBUTOS como referencia. Llena las hojas "Productos" e "Items".'}
                {importType === 'items' && '📄 Importa items de productos existentes usando CSV.'}
                {importType === 'products' && '📄 Importa solo productos usando CSV.'}
                {importType === 'combined' && '📄 Importa productos e items en un solo archivo CSV.'}
              </Form.Text>
            </Form.Group>
          </div>

          {/* Paso 2: Descargar plantilla */}
          <div className="import-step mb-4">
            <h5 className="step-title">
              <Badge bg="primary" className="me-2">2</Badge>
              Descarga la Plantilla {importType.startsWith('excel-') ? 'Excel' : 'CSV'}
            </h5>
            <Button 
              variant="outline-primary" 
              onClick={handleDownloadTemplate}
              disabled={importing || generatingTemplate}
              size="lg"
            >
              {generatingTemplate ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando datos del sistema...
                </>
              ) : (
                <>
                  <i className={`fas fa-${importType.startsWith('excel-') ? 'file-excel' : 'file-csv'} me-2`}></i>
                  Descargar Plantilla {importType.startsWith('excel-') ? 'Excel' : 'CSV'}
                </>
              )}
            </Button>
            <Alert variant="info" className="mt-3 mb-0">
              <i className="fas fa-info-circle me-2"></i>
              {importType === 'excel-items' && (
                <>
                  <strong>📦 Plantilla para Items:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Hoja 1 - Productos (Referencia):</strong> Lista de productos existentes para que sepas qué productos puedes usar</li>
                    <li><strong>Hoja 2 - Atributos (Referencia):</strong> Colores, tallas y gramajes disponibles</li>
                    <li><strong>Hoja 3 - Items:</strong> <Badge bg="success">LLENA ESTA HOJA</Badge> con los items a crear</li>
                  </ul>
                  <div className="mt-2">
                    <strong>💡 Tip:</strong> Usa los nombres de producto exactamente como aparecen en la hoja de referencia.
                  </div>
                </>
              )}
              {importType === 'excel-products' && (
                <>
                  <strong>📋 Plantilla para Productos:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Hoja 1 - Categorías (Referencia):</strong> Lista de categorías existentes para que uses los nombres correctos</li>
                    <li><strong>Hoja 2 - Productos:</strong> <Badge bg="success">LLENA ESTA HOJA</Badge> con los productos a crear</li>
                  </ul>
                  <div className="mt-2">
                    <strong>💡 Tip:</strong> Usa los nombres de categoría exactamente como aparecen en la hoja de referencia.
                  </div>
                </>
              )}
              {importType === 'excel-combined' && (
                <>
                  <strong>📊 Plantilla para Productos + Items:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Hoja 1 - Categorías (Referencia):</strong> Categorías disponibles</li>
                    <li><strong>Hoja 2 - Atributos (Referencia):</strong> Colores, tallas y gramajes disponibles</li>
                    <li><strong>Hoja 3 - Productos:</strong> <Badge bg="success">LLENA</Badge> con productos a crear</li>
                    <li><strong>Hoja 4 - Items:</strong> <Badge bg="success">LLENA</Badge> con items a crear</li>
                  </ul>
                  <div className="mt-2">
                    <strong>💡 Tip:</strong> Primero se crean los productos, luego los items. Los items pueden usar productos de la hoja Productos.
                  </div>
                </>
              )}
              {!importType.startsWith('excel-') && (
                <>
                  <strong>Importante:</strong> Llena la plantilla CSV con tus datos. Las imágenes se pueden agregar después editando cada item.
                </>
              )}
            </Alert>
          </div>

          {/* Paso 3: Subir archivo */}
          <div className="import-step mb-4">
            <h5 className="step-title">
              <Badge bg="primary" className="me-2">3</Badge>
              Sube tu Archivo {importType.startsWith('excel-') ? 'Excel' : 'CSV'}
            </h5>
            <Form.Control
              id="fileInput"
              type="file"
              accept={importType.startsWith('excel-') ? '.xlsx,.xls' : '.csv'}
              onChange={handleFileUpload}
              className="modern-input"
              disabled={importing}
            />
            {csvFile && (
              <div className="mt-2">
                <Badge bg="success">
                  <i className={`fas fa-${importType.startsWith('excel-') ? 'file-excel' : 'file-csv'} me-1`}></i>
                  {csvFile.name}
                </Badge>
                {importType.startsWith('excel-') && validationResults && (
                  <div className="mt-2">
                    {importType !== 'excel-items' && (
                      <Badge bg="info" className="me-2">
                        📦 {validationResults.productsCount || 0} productos a crear
                      </Badge>
                    )}
                    {importType !== 'excel-products' && (
                      <Badge bg="info">
                        📋 {validationResults.itemsCount || 0} items a crear
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Vista previa y validación */}
          {((csvData.length > 0 || excelData.products.length > 0 || excelData.items.length > 0) && validationResults) && (
            <div className="import-step mb-4">
              <h5 className="step-title">
                <Badge bg="primary" className="me-2">4</Badge>
                Vista Previa y Validación
              </h5>

              {/* Resultados de validación */}
              <Row className="mb-3">
                <Col md={4}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h3 className="text-success mb-0">
                        {importType === 'excel' 
                          ? (excelData.products.length + excelData.items.length)
                          : csvData.length
                        }
                      </h3>
                      <small className="text-muted">Registros Totales</small>
                      {importType === 'excel' && (
                        <div className="mt-2">
                          <small className="text-muted d-block">
                            {excelData.products.length} productos
                          </small>
                          <small className="text-muted d-block">
                            {excelData.items.length} items
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-danger">
                    <Card.Body>
                      <h3 className="text-danger mb-0">
                        {validationResults.dataValidation.errors.length}
                      </h3>
                      <small className="text-muted">Errores</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h3 className="text-warning mb-0">
                        {validationResults.dataValidation.warnings.length}
                      </h3>
                      <small className="text-muted">Advertencias</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Errores de validación */}
              {validationResults.dataValidation.errors.length > 0 && (
                <Alert variant="danger">
                  <strong>Errores encontrados:</strong>
                  <ul className="mb-0 mt-2">
                    {validationResults.dataValidation.errors.slice(0, 10).map((error, index) => (
                      <li key={index}>
                        {error.sheet && <strong>[{error.sheet}]</strong>} Fila {error.row}, Campo "{error.field}": {error.message}
                      </li>
                    ))}
                    {validationResults.dataValidation.errors.length > 10 && (
                      <li>... y {validationResults.dataValidation.errors.length - 10} errores más</li>
                    )}
                  </ul>
                </Alert>
              )}

              {/* Advertencias */}
              {validationResults.dataValidation.warnings.length > 0 && (
                <Alert variant="warning">
                  <strong>Advertencias:</strong>
                  <ul className="mb-0 mt-2">
                    {validationResults.dataValidation.warnings.slice(0, 5).map((warning, index) => (
                      <li key={index}>
                        Fila {warning.row}, Campo "{warning.field}": {warning.message}
                      </li>
                    ))}
                    {validationResults.dataValidation.warnings.length > 5 && (
                      <li>... y {validationResults.dataValidation.warnings.length - 5} advertencias más</li>
                    )}
                  </ul>
                </Alert>
              )}

              {/* Tabla de vista previa */}
              {importType.startsWith('excel-') ? (
                <>
                  {/* Vista previa de Productos (solo si aplica) */}
                  {(importType === 'excel-products' || importType === 'excel-combined') && excelData.products.length > 0 && (
                    <div className="mb-4">
                      <h6 className="fw-bold">
                        <i className="fas fa-box me-2"></i>
                        Productos ({excelData.products.length})
                      </h6>
                      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Table striped bordered hover className="modern-table" size="sm">
                          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th style={{ width: '50px' }}>Fila</th>
                              <th>Nombre</th>
                              <th>Descripción</th>
                              <th>Categoría</th>
                              <th>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {excelData.products.slice(0, 50).map((row, index) => {
                              const hasError = validationResults.dataValidation.errors.some(
                                err => err.sheet === 'Productos' && err.row === index + 2
                              );
                              return (
                                <tr key={index} className={hasError ? 'table-danger' : ''}>
                                  <td>{index + 1}</td>
                                  <td>{row.nombre_producto || '-'}</td>
                                  <td>{(row.descripcion_producto || '-').substring(0, 40)}...</td>
                                  <td>{row.categoria || '-'}</td>
                                  <td>
                                    {hasError ? (
                                      <Badge bg="danger">Error</Badge>
                                    ) : (
                                      <Badge bg="success">Válido</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Vista previa de Items (solo si aplica) */}
                  {(importType === 'excel-items' || importType === 'excel-combined') && excelData.items.length > 0 && (
                    <div>
                      <h6 className="fw-bold">
                        <i className="fas fa-cube me-2"></i>
                        Items ({excelData.items.length})
                      </h6>
                      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Table striped bordered hover className="modern-table" size="sm">
                          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th style={{ width: '50px' }}>Fila</th>
                              <th>ID Producto</th>
                              <th>Producto</th>
                              <th>Descripción</th>
                              <th>Precio</th>
                              <th>Stock</th>
                              <th>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {excelData.items.slice(0, 50).map((row, index) => {
                              const hasError = validationResults.dataValidation.errors.some(
                                err => err.sheet === 'Items' && err.row === index + 2
                              );
                              return (
                                <tr key={index} className={hasError ? 'table-danger' : ''}>
                                  <td>{index + 1}</td>
                                  <td>{row.id_product ?? row.id_producto ?? '-'}</td>
                                  <td>{row.producto ?? row.nombre_producto ?? '-'}</td>
                                  <td>{(row.descripcion_item || '-').substring(0, 30)}...</td>
                                  <td>${row.precio ?? 0}</td>
                                  <td>{row.stock ?? 0}</td>
                                  <td>
                                    {hasError ? (
                                      <Badge bg="danger">Error</Badge>
                                    ) : (
                                      <Badge bg="success">Válido</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table striped bordered hover className="modern-table">
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: '50px' }}>Fila</th>
                        {csvHeaders.slice(0, 6).map((header, index) => (
                          <th key={index}>{header}</th>
                        ))}
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.slice(0, 50).map((row, index) => {
                        const hasError = validationResults.dataValidation.errors.some(
                          err => err.row === index + 2
                        );
                        return (
                          <tr key={index} className={hasError ? 'table-danger' : ''}>
                            <td>{index + 1}</td>
                            {csvHeaders.slice(0, 6).map((header, colIndex) => (
                              <td key={colIndex}>{row[header] || '-'}</td>
                            ))}
                            <td>
                              {hasError ? (
                                <Badge bg="danger">Error</Badge>
                              ) : (
                                <Badge bg="success">Válido</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  {csvData.length > 50 && (
                    <div className="text-center text-muted py-2">
                      Mostrando primeras 50 filas de {csvData.length}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Barra de progreso */}
          {importing && (
            <div className="mb-4">
              <ProgressBar 
                now={importProgress} 
                label={`${Math.round(importProgress)}%`}
                animated
                variant="success"
              />
              <div className="text-center mt-2">
                <Spinner animation="border" size="sm" className="me-2" />
                Importando datos...
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="d-flex gap-2 justify-content-end">
            <Button 
              variant="secondary" 
              onClick={handleReset}
              disabled={
                importing || 
                (importType.startsWith('excel-') 
                  ? (excelData.products.length === 0 && excelData.items.length === 0)
                  : csvData.length === 0
                )
              }
            >
              <i className="fas fa-redo me-2"></i>
              Reiniciar
            </Button>
            <Button
              variant="primary"
              className="modern-btn-primary"
              onClick={handleImport}
              disabled={
                !validationResults?.isValid || 
                importing || 
                (importType.startsWith('excel-') 
                  ? (excelData.products.length === 0 && excelData.items.length === 0)
                  : csvData.length === 0
                )
              }
            >
              {importing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Importando...
                </>
              ) : (
                <>
                  <i className="fas fa-upload me-2"></i>
                  Importar {
                    importType.startsWith('excel-') 
                      ? (excelData.products.length + excelData.items.length)
                      : csvData.length
                  } Registros
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Modal de resultados */}
      <Modal 
        show={showResults} 
        onHide={() => setShowResults(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="fas fa-check-circle me-2"></i>
            Resultados de Importación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success">
            <h5>
              <i className="fas fa-check me-2"></i>
              {importResults.success} registros importados exitosamente
            </h5>
          </Alert>

          {/* Resumen de ítems creados */}
          {importResults.createdItems?.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-2">
                <i className="fas fa-box me-2"></i>
                Ítems creados ({importResults.createdItems.length})
              </h6>
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      {importType.startsWith('excel-') && <th>Hoja</th>}
                      <th>Fila</th>
                      <th>Producto</th>
                      <th>Descripción</th>
                      <th>Precio</th>
                      <th>Stock</th>
                      <th>SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResults.createdItems.map((item, index) => (
                      <tr key={index}>
                        {importType.startsWith('excel-') && <td>{item.sheet || 'Items'}</td>}
                        <td>{item.row}</td>
                        <td>{item.product || '-'}</td>
                        <td title={item.description}>{(item.description || '-').substring(0, 35)}{(item.description?.length > 35) ? '…' : ''}</td>
                        <td>{typeof item.price === 'number' ? item.price.toLocaleString() : item.price}</td>
                        <td>{item.stock ?? '-'}</td>
                        <td>{item.sku || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* Resumen de productos creados (si aplica) */}
          {importResults.createdProducts?.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-2">
                <i className="fas fa-tag me-2"></i>
                Productos creados ({importResults.createdProducts.length})
              </h6>
              <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      {importType.startsWith('excel-') && <th>Hoja</th>}
                      <th>Fila</th>
                      <th>Nombre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResults.createdProducts.map((prod, index) => (
                      <tr key={index}>
                        {importType.startsWith('excel-') && <td>{prod.sheet || 'Productos'}</td>}
                        <td>{prod.row}</td>
                        <td>{prod.name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {importResults.errors.length > 0 && (
            <>
              <Alert variant="danger">
                <h5>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {importResults.errors.length} errores encontrados
                </h5>
              </Alert>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      {importType.startsWith('excel-') && <th>Hoja</th>}
                      <th>Fila</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResults.errors.map((error, index) => (
                      <tr key={index}>
                        {importType.startsWith('excel-') && <td>{error.sheet || '-'}</td>}
                        <td>{error.row}</td>
                        <td>{error.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResults(false)}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleReset}>
            Nueva Importación
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BulkImportTab;
